import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MoreHorizontal } from 'lucide-react';
import { AlertDialogComponent } from '@/components/alert-dialog';
import { deleteClient } from '@/actions';
import { TClientData } from '@/types/client';
import { Pen, Trash } from 'lucide-react';
import { useClientContext } from '@/contexts/client-context';
import { useSubmitStatus } from '@/hooks/useSubmitStatus';
import { LoadingButton } from '@/components/loading-button';

interface Props {
  client: TClientData;
  t: (key: string) => string;
}

export const ClientActions = ({ client, t }: Props) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { dispatch } = useClientContext();
  const { status, wrap } = useSubmitStatus();

  const handleDelete = () => {
    wrap(async () => {
      const result = await deleteClient(client.id);
      if (result.error) {
        console.error('Error deleting client:', result.error);
        throw new Error(result.error);
      }
    }).then(() => {
      setTimeout(() => {
        setIsDialogOpen(false);
      }, 1000);
    });
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="h-8 w-8 p-0"
            aria-label="Open menu"
          >
            <span className="sr-only">{t('openMenu')}</span>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end">
          <DropdownMenuLabel>{t('actions')}</DropdownMenuLabel>
          <DropdownMenuSeparator />

          <DropdownMenuItem
            className="cursor-pointer"
            onClick={() => {
              dispatch({
                type: 'OPEN_EDIT_CLIENT_POPUP',
                payload: { clientId: client.id },
              });
            }}
          >
            <Pen width={5} height={5} />
            <span>{t('editClient')}</span>
          </DropdownMenuItem>

          <DropdownMenuItem
            className="cursor-pointer"
            onClick={() => {
              requestAnimationFrame(() => setIsDialogOpen(true));
            }}
          >
            <Trash width={5} height={5} />
            <span>{t('deleteClient')}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialogComponent
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        title={t('deleteClientAlert.title')}
        description={t('deleteClientAlert.description')}
        cancelLabel={t('deleteClientAlert.cancelLabel')}
        deleteLabel={
          <LoadingButton
            isFull={false}
            type="button"
            status={status}
            idleText={t('deleteClientAlert.deleteLabel')}
            loadingText="Deleting..."
            successText="Deleted"
            errorText="Failed"
            onClick={handleDelete}
          />
        }
      />
    </>
  );
};
